import React, { FC, useMemo, useCallback } from 'react';
import { Formik, getIn } from 'formik';
import { useHistory } from 'react-router-dom';
import urlJoin from 'url-join';
import * as Yup from 'yup';
import { Button } from '@clearcover/atlas';

import { reportEvent } from 'Shared/analytics';
import { Column, FormInput, Row, useToast } from 'Shared/components';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';
import {
  STATE_OPTIONS,
  INDEPENDENT_AGENT_STATES,
  VALID_US_STATES_TYPE,
} from 'Shared/constants/states';
import {
  useLazyQuoteApi,
  useQuoteApi,
} from 'Shared/services/Api/makeQuoteApiHttp';
import { Address, ScreenHeader, Body } from 'QuoteApp/components';
import {
  transformDisclosuresResponse,
  GetDisclosuresResponseType,
  Disclosures,
} from 'QuoteApp/services/endpoints/GetDisclosures';
import {
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  transformRequest,
  transformPostStartStepResponse,
  PostStartResponseType,
  StartFormDriverValuesType,
  AddressValuesType,
} from 'QuoteApp/services/endpoints/PostStartStep';
import {
  yupDOBValidator,
  monthDateYearNormalizer,
  getErrorMessage,
  addressValidation,
} from 'QuoteApp/utils';
import { useAppContext } from 'QuoteApp/services/providers/AppProvider';
import { isAgentUnauthorized } from 'QuoteApp/utils/hooks/useAgentUnauthorized';

import styles from './styles.module.css';

export const Start: FC = () => {
  const { replace } = useHistory();
  const { appId, details, refreshDetails, setDetails } = useAppContext();
  const { toast } = useToast();
  const [, postStart] = useLazyQuoteApi<PostStartResponseType>({
    method: 'POST',
    url: `/insurance_applications/${appId}/start`,
    transformResponse: transformPostStartStepResponse,
  });

  const [, postDisclosure] = useLazyQuoteApi<GetDetailsResponseType>({
    method: 'POST',
    url: `/insurance_applications/${appId}/document_signatures/${Disclosures.Initial}/complete`,
    transformResponse: transformAppDetails,
  });

  const [{ data: disclosureData, error }] = useQuoteApi<
    GetDisclosuresResponseType
  >(
    {
      url: `insurance_applications/${appId}/document_signatures/${Disclosures.Initial}`,
      transformResponse: transformDisclosuresResponse,
    },
    false,
    false // do not throw error on first load, so we can handle it below
  );

  const getFullFieldName = useCallback(
    (
      section: 'driver' | 'address',
      fieldName: keyof StartFormDriverValuesType | keyof AddressValuesType
    ) => `${section}.${details.principalInsuredId}.${fieldName}`,
    [details.principalInsuredId]
  );

  const startFormValidation = useMemo(
    () =>
      Yup.object().shape({
        driver: Yup.object().shape({
          [details.principalInsuredId]: Yup.object().shape({
            firstName: Yup.string().required('Required'),
            middleName: Yup.string(),
            lastName: Yup.string().required('Required'),
            dateOfBirth: yupDOBValidator().required('Required'),
            suffix: Yup.string(),
          }),
        }),
        address: addressValidation(details.principalInsuredId),
      }),
    [details.principalInsuredId]
  );

  const filteredStateOptions = useMemo(
    () =>
      Object.entries(STATE_OPTIONS).reduce(
        (filtered, [stateAbbreviation, stateName]) => {
          if (
            INDEPENDENT_AGENT_STATES.includes(
              stateAbbreviation as VALID_US_STATES_TYPE
            )
          ) {
            return {
              ...filtered,
              [stateAbbreviation]: stateName,
            };
          }

          return filtered;
        },
        {}
      ),
    []
  );

  return (
    <Body data-testid="start-page">
      <ScreenHeader
        headerText="Get Started"
        // once adpf is supported, put this back in
        // subHeaderText="Clearcover has made driver and vehicle look up easy for our
        // agent partners. Simply fill out the information needed below and we will do
        // our best to pre-fill the remaining steps of the application with the customer
        // data from 3rd party reports."
      />

      <div className={styles.formContainer}>
        <Formik
          initialValues={{
            identifier: details.principalInsuredId,
            driver: {
              [details.principalInsuredId]: {
                firstName: '',
                middleName: '',
                lastName: '',
                suffix: '',
                dateOfBirth: '',
              },
            },
            address: {
              [details.principalInsuredId]: {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
              },
            },
          }}
          initialStatus={{
            isInvalidAddress: false,
          }}
          onSubmit={async (
            data,
            { setSubmitting, setStatus, setFieldError }
          ) => {
            try {
              const response = await postStart({
                data,
                transformRequest,
              });

              // Handle any state that should route the user elsewhere
              // except for unauthorized, which is handled below
              if (response.data.appDetails.status !== 'open') {
                setDetails(response.data.appDetails);
                return;
              }

              try {
                await refreshDetails(); // to confirm if agent is authorized
              } catch (e) {
                throw new Error(getErrorMessage(e));
              }

              if (response.data.isAddressValid) {
                replace(urlJoin(QUOTE_APP_ROUTE, appId, '/drivers'));
              } else {
                if (response.data.isMissingLineTwo) {
                  setFieldError(
                    getFullFieldName('address', 'address2'),
                    'Line 2 is required'
                  );
                } else {
                  reportEvent('aqui-invalid-address-notification-displayed');
                  setStatus({ isInvalidAddress: true });
                }
                setSubmitting(false);
              }
            } catch (e) {
              if (!isAgentUnauthorized(e)) {
                // if unauthorized, refreshDetails will route to Not Authorized page
                toast({
                  type: 'error',
                  message: getErrorMessage(e),
                  withSecondaryNav: false,
                  icon: 'error',
                });
                setSubmitting(false);
              }
            }
          }}
          validationSchema={startFormValidation}
          validateOnMount
        >
          {({
            setFieldValue,
            handleChange,
            handleBlur,
            values,
            touched,
            errors,
            isSubmitting,
            handleSubmit,
            isValid,
            status,
          }) => {
            return (
              <>
                <Row>
                  <Column variant="third">
                    <FormInput
                      data-testid="first-name"
                      name={getFullFieldName('driver', 'firstName')}
                      variant="text"
                      label="First Name"
                      value={
                        values.driver[details.principalInsuredId].firstName
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        getIn(touched, getFullFieldName('driver', 'firstName'))
                          ? getIn(
                              errors,
                              getFullFieldName('driver', 'firstName')
                            )
                          : undefined
                      }
                    />
                  </Column>
                  <Column variant="sixth">
                    <FormInput
                      data-testid="middle-initial"
                      name={getFullFieldName('driver', 'middleName')}
                      variant="text"
                      label="MI"
                      value={
                        values.driver[details.principalInsuredId].middleName
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Column>
                  <Column variant="third">
                    <FormInput
                      data-testid="last-name"
                      name={getFullFieldName('driver', 'lastName')}
                      variant="text"
                      label="Last Name"
                      value={values.driver[details.principalInsuredId].lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        getIn(touched, getFullFieldName('driver', 'lastName'))
                          ? getIn(
                              errors,
                              getFullFieldName('driver', 'lastName')
                            )
                          : undefined
                      }
                    />
                  </Column>
                  <Column variant="sixth">
                    <FormInput
                      data-testid="suffix"
                      name={getFullFieldName('driver', 'suffix')}
                      variant="selectAutocomplete"
                      label="Suffix"
                      value={values.driver[details.principalInsuredId].suffix}
                      onChange={(value: string) =>
                        setFieldValue(
                          getFullFieldName('driver', 'suffix'),
                          value
                        )
                      }
                      onBlur={handleBlur}
                      // Adding "none" option so Suffix is clearable
                      options={{
                        none: '',
                        Jr: 'Jr',
                        Sr: 'Sr',
                        I: 'I',
                        II: 'II',
                        III: 'III',
                        IV: 'IV',
                      }}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column variant="third">
                    <FormInput
                      data-testid="date-of-birth"
                      name={getFullFieldName('driver', 'dateOfBirth')}
                      variant="text"
                      label="Date of Birth"
                      placeholder="MM/DD/YYYY"
                      value={
                        values.driver[details.principalInsuredId].dateOfBirth
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue(
                          getFullFieldName('driver', 'dateOfBirth'),
                          monthDateYearNormalizer(e.currentTarget.value)
                        );
                      }}
                      onBlur={handleBlur}
                      errorMessage={
                        getIn(
                          touched,
                          getFullFieldName('driver', 'dateOfBirth')
                        )
                          ? getIn(
                              errors,
                              getFullFieldName('driver', 'dateOfBirth')
                            )
                          : undefined
                      }
                    />
                  </Column>
                  <Column variant="third" />
                </Row>
                <Address stateOptions={filteredStateOptions} />
                <div className={styles.highlightSection}>
                  <div
                    className={styles.disclosure}
                    dangerouslySetInnerHTML={disclosureData?.bodyTextSanitized}
                  />
                  <Button
                    data-testid="start-page-continue"
                    variant="primary"
                    type="button"
                    onClick={async () => {
                      // if the user has seen the disclosure
                      if (disclosureData && !error) {
                        // post to the disclosure endpoint
                        try {
                          const completedDisclosureData = await postDisclosure();

                          // refresh updated details
                          setDetails(completedDisclosureData.data);
                        } catch (e) {
                          toast({
                            type: 'error',
                            message: `Error capturing disclosure: ${getErrorMessage(
                              e
                            )}`,
                            withSecondaryNav: false,
                            icon: 'error',
                          });
                        }
                      }

                      // do not block submission
                      handleSubmit();
                    }}
                    disabled={
                      isSubmitting || !isValid || status.isInvalidAddress
                    }
                    isSubmitting={isSubmitting}
                  >
                    Continue
                  </Button>
                </div>
              </>
            );
          }}
        </Formik>
      </div>
    </Body>
  );
};
